import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDownIcon, CheckIcon } from '@radix-ui/react-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { PromptAgentType } from './types';
import { getAgentTypes, getAgentTypeLabel } from './promptLibraryData';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { SearchInput } from '../../components/common/SearchInput';

interface AgentTypeSelectProps {
  /** Single-select mode: currently selected value */
  value?: PromptAgentType | null;
  /** Single-select callback */
  onChange?: (type: PromptAgentType) => void;
  /** Multi-select mode: currently selected values */
  selectedValues?: PromptAgentType[];
  /** Multi-select callback */
  onSelectedValuesChange?: (values: PromptAgentType[]) => void;
  /** Selection mode */
  selectMode?: 'single' | 'multi';
  /** Placeholder text when nothing is selected */
  placeholder?: string;
  /** Whether to stretch to fill the parent container width */
  fullWidth?: boolean;
}

export const AgentTypeSelect: React.FC<AgentTypeSelectProps> = ({
  value,
  onChange,
  selectedValues = [],
  onSelectedValuesChange,
  selectMode = 'single',
  placeholder = 'Chat Type',
  fullWidth = false,
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const agentTypes = useMemo(() => getAgentTypes(), []);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number>(0);

  const isMulti = selectMode === 'multi';
  const isAllSelected = isMulti && selectedValues.length === 0;

  const displayLabel = isMulti
    ? isAllSelected
      ? placeholder
      : selectedValues.length === 1
        ? getAgentTypeLabel(selectedValues[0])
        : `${selectedValues.length} selected`
    : value ? getAgentTypeLabel(value) : placeholder;

  // Measure trigger width for dropdown matching
  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [isOpen, displayLabel]);

  // Categorize chat types
  const genericAiChat = useMemo(() => agentTypes.filter(t => t === 'Ask Chat'), [agentTypes]);
  const knowledgeTypes = useMemo(() => agentTypes.filter(t => t !== 'Ask Chat'), [agentTypes]);

  // Filter by search text (match against label)
  const filteredGeneric = useMemo(() => {
    if (!searchText.trim()) return genericAiChat;
    const q = searchText.toLowerCase();
    return genericAiChat.filter(t => getAgentTypeLabel(t).toLowerCase().includes(q));
  }, [genericAiChat, searchText]);

  const filteredKnowledge = useMemo(() => {
    if (!searchText.trim()) return knowledgeTypes;
    const q = searchText.toLowerCase();
    return knowledgeTypes.filter(t => getAgentTypeLabel(t).toLowerCase().includes(q));
  }, [knowledgeTypes, searchText]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchText('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearchText('');
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSelectType = (type: PromptAgentType) => {
    if (isMulti) {
      if (selectedValues.includes(type)) {
        onSelectedValuesChange?.(selectedValues.filter((t) => t !== type));
      } else {
        onSelectedValuesChange?.([...selectedValues, type]);
      }
    } else {
      onChange?.(type);
      setIsOpen(false);
      setSearchText('');
    }
  };

  const renderOption = (type: PromptAgentType) => {
    const isSelected = isMulti
      ? selectedValues.includes(type)
      : value === type;
    return (
      <button
        key={type}
        role="option"
        aria-selected={isSelected}
        onClick={() => handleSelectType(type)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors"
        style={{ background: 'transparent', fontFamily: '3MCircularTT, sans-serif' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255, 255, 255, 0.12)' : '#BFDBFE'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <span
          className="flex items-center justify-center flex-shrink-0 rounded"
          style={{
            width: 16,
            height: 16,
            border: isSelected
              ? 'none'
              : `1.5px solid ${isDark ? 'rgba(255,255,255,0.3)' : '#9ca3af'}`,
            background: isSelected
              ? isDark ? '#82B6FA' : '#2563EB'
              : 'transparent',
          }}
        >
          {isSelected && <CheckIcon width={12} height={12} style={{ color: '#fff' }} />}
        </span>
        <span
          style={{
            fontWeight: isSelected ? 600 : 400,
            marginTop: 2,
            color: isSelected
              ? isDark ? '#82B6FA' : '#2563EB'
              : isDark ? 'rgba(255,255,255,0.85)' : '#374151',
          }}
        >
          {getAgentTypeLabel(type)}
        </span>
      </button>
    );
  };

  return (
    <div ref={containerRef} className={`relative ${fullWidth ? 'block w-full' : 'inline-block'}`}>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex items-center justify-between gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${fullWidth ? 'w-full' : ''}`}
        style={{
          ...(!fullWidth && { minWidth: '200px', maxWidth: '280px' }),
          color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#5E95ED',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid #5E95ED',
          background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#E8F3FF',
          fontFamily: '3MCircularTT, sans-serif',
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span
          className="truncate top-heavy"
          style={{ textAlign: 'left', lineHeight: '1.3' }}
        >
          {displayLabel}
        </span>
        <ChevronDownIcon
          className="w-4 h-4 transition-transform flex-shrink-0"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {/* Dropdown menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scaleY: 0.8, y: -4 }}
            animate={{ opacity: 1, scaleY: 1, y: 0 }}
            exit={{ opacity: 0, scaleY: 0.8, y: -4 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className={`absolute left-0 mt-1 rounded-lg shadow-lg overflow-hidden origin-top scrollbar-hide ${fullWidth ? 'w-full' : ''}`}
            style={{
              ...(!fullWidth && { width: Math.max(triggerWidth, 200) }),
              maxHeight: '360px',
              overflowY: 'auto',
              scrollBehavior: 'smooth',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              background: isDark ? 'rgba(30, 30, 30, 0.95)' : '#FFFFFF',
              border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #5E95ED',
              backdropFilter: 'blur(12px)',
              zIndex: 200,
            }}
            role="listbox"
            aria-multiselectable={isMulti}
          >
            {/* Search input */}
            <div className="px-2 pt-2 pb-1 sticky top-0" style={{ background: isDark ? 'rgba(30, 30, 30, 0.95)' : '#FFFFFF' }}>
              <SearchInput
                value={searchText}
                onChange={setSearchText}
                placeholder="Search"
                forceDark={isDark}
                iconSize={14}
                containerClassName="w-full"
                inputClassName="!py-1.5 !text-xs"
              />
            </div>

            {/* Generic AI Chat section */}
            {filteredGeneric.length > 0 && (
              <>
                <div
                  className="px-3 pt-2 pb-1 text-xs font-semibold"
                  style={{
                    color: isDark ? '#5E95ED' : '#2563EB',
                    fontFamily: '3MCircularTT, sans-serif',
                  }}
                >
                  Generic AI Chat
                </div>
                {filteredGeneric.map(renderOption)}
              </>
            )}

            {/* 3M Knowledge section */}
            {filteredKnowledge.length > 0 && (
              <>
                <div
                  className="px-3 pt-2 pb-1 text-xs font-semibold"
                  style={{
                    color: isDark ? '#5E95ED' : '#2563EB',
                    fontFamily: '3MCircularTT, sans-serif',
                  }}
                >
                  3M Knowledge
                </div>
                {filteredKnowledge.map(renderOption)}
              </>
            )}

            {/* No results */}
            {filteredGeneric.length === 0 && filteredKnowledge.length === 0 && (
              <div
                className="px-3 py-3 text-xs text-center"
                style={{ color: isDark ? '#71717a' : '#9ca3af', fontFamily: '3MCircularTT, sans-serif' }}
              >
                No matching chat types
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
